/**
 * Minimal Scroll Snap Carousel - Pure Vanilla JS
 * Lightweight replacement for owl.carousel using CSS scroll-snap
 * Handles only: arrow navigation + show/hide arrows at boundaries
 */

(function() {
    'use strict';

    // Debounce utility function
    function debounce(func, wait) {
        let timeout;
        return function executedFunction() {
            const context = this;
            const args = arguments;
            clearTimeout(timeout);
            timeout = setTimeout(function() {
                func.apply(context, args);
            }, wait);
        };
    }

    function isReviewSectionCarousel(carousel) {
        return !!carousel.closest('.c-Home__section--reviews');
    }

    function setReviewCarouselWidths(carousel) {
        const stage = carousel.querySelector('.scroll-snap-stage');
        if (!stage) return;

        const items = Array.from(stage.children);
        if (!items.length) return;

        items.forEach(function(item) {
            item.style.aspectRatio = '';
            item.style.width = '';
            item.style.flex = '';
        });

        if (window.innerWidth < 1260) return;

        const section = carousel.closest('.c-Home__section--reviews');
        const ratingContainer = section && section.querySelector('.c-Reviews__rating-cntr');
        const gap = parseFloat(getComputedStyle(stage).gap || 0);
        if (!ratingContainer) return;

        const itemWidth = (ratingContainer.getBoundingClientRect().width - (gap * 2)) / 3;

        items.forEach(function(item) {
            item.style.aspectRatio = 'auto';
            item.style.width = itemWidth + 'px';
            item.style.flex = '0 0 ' + itemWidth + 'px';
        });
    }

    // Normalize carousel widths for small item count carousels on desktop
    function normalizeCarouselWidths(root) {
        root = root || document;
        const isDesktop = window.innerWidth > 1259;
        // The homepage will now rely entirely on its CSS widths. Other unmigrated pages continue using the old normalization behavior.
        const carousels = root.querySelectorAll('.owl-carousel[data-carousel-column="3"]' + ':not([data-scroll-snap-carousel])');

        if (!isDesktop) {
            // Remove fixed widths on mobile/tablet
            carousels.forEach(function(carousel) {
                const stage = carousel.querySelector('.scroll-snap-stage');
                if (!stage) return;

                const items = stage.children;
                Array.from(items).forEach(function(item) {
                    item.style.width = '';
                    item.style.flex = '';
                });
            });
            return;
        }

        // Find reference width from carousel with 5+ items
        let referenceWidth = null;
        carousels.forEach(function(carousel) {
            if (isReviewSectionCarousel(carousel)) {
                setReviewCarouselWidths(carousel);
                return;
            }

            const stage = carousel.querySelector('.scroll-snap-stage');
            if (!stage) return;

            const itemCount = stage.children.length;
            if (itemCount >= 5 && !referenceWidth) {
                const firstItem = stage.children[0];
                if (firstItem) {
                    referenceWidth = firstItem.offsetWidth;
                }
            }
        });

        // Apply reference width to carousels with < 5 items
        carousels.forEach(function(carousel) {
            if (isReviewSectionCarousel(carousel)) return;

            const stage = carousel.querySelector('.scroll-snap-stage');
            if (!stage) return;

            const itemCount = stage.children.length;
            const items = stage.children;

            if (itemCount < 5 && referenceWidth) {
                // Apply fixed width from reference carousel
                Array.from(items).forEach(function(item) {
                    item.style.width = referenceWidth + 'px';
                    item.style.flex = '0 0 ' + referenceWidth + 'px';
                });
            } else {
                // Remove inline styles for carousels with 5+ items
                Array.from(items).forEach(function(item) {
                    item.style.width = '';
                    item.style.flex = '';
                });
            }
        });
    }

    // Destroy carousel and restore original structure
    function destroyScrollSnapCarousel(carousel) {
        if (!carousel || !carousel.classList.contains('scroll-snap-initialized')) {
            return;
        }

        const stage = carousel.querySelector('.scroll-snap-stage');
        if (!stage) return;

        // Move all items back to carousel (unwrap from stage)
        const items = Array.from(stage.children);
        items.forEach(item => {
            item.style.width = '';
            item.style.flex = '';
            carousel.appendChild(item);
        });

        // Remove stage element
        stage.remove();

        // Remove initialized class
        carousel.classList.remove('scroll-snap-initialized');
        carousel.classList.add('owl-loading');

        // Note: Event listeners will be garbage collected when elements are removed
    }

    // server-rendered homepage carousels.
    const serverRenderedCarouselInstances = new Map();

    const serverRenderedCarouselResizeObserver =
        'ResizeObserver' in window
            ? new ResizeObserver(function(entries) {
                entries.forEach(function(entry) {
                    const instance =
                        serverRenderedCarouselInstances.get(entry.target);

                    if (instance) {
                        instance.scheduleArrowUpdate();
                    }
                });
            })
            : null;

    function setArrowDisabled(button, disabled) {
        if (!button) return;

        button.classList.toggle('disabled', disabled);
        button.setAttribute(
            'aria-disabled',
            disabled ? 'true' : 'false'
        );
    }

    function initServerRenderedCarousels() {
        const carousels = document.querySelectorAll(
            '[data-scroll-snap-carousel]:not([data-scroll-snap-bound])'
        );

        carousels.forEach(function(carousel) {
            const wrapper = carousel.closest('.c-Carousel__wrap');

            // The stage already exists in the server-generated HTML.
            const stage = carousel.querySelector(
                ':scope > .scroll-snap-stage'
            );

            if (!wrapper || !stage) return;

            const prevButton = wrapper.querySelector(
                '.c-Carousel__toggle-prev'
            );

            const nextButton = wrapper.querySelector(
                '.c-Carousel__toggle-next'
            );

            const itemsPerView =
                parseInt(carousel.dataset.carouselColumn, 10) || 1;

            let pendingArrowFrame = null;

            function updateArrows() {
                pendingArrowFrame = null;

                const maxScroll =
                    stage.scrollWidth - stage.clientWidth;

                setArrowDisabled(
                    prevButton,
                    stage.scrollLeft <= 1
                );

                setArrowDisabled(
                    nextButton,
                    stage.scrollLeft >= maxScroll - 1
                );
            }

            function scheduleArrowUpdate() {
                if (pendingArrowFrame !== null) return;

                pendingArrowFrame =
                    window.requestAnimationFrame(updateArrows);
            }

            /*
            * This layout read happens only after a user clicks an arrow.
            * It does not contribute to initial page rendering or TBT.
            */
            function getItemStep() {
                const firstItem = stage.firstElementChild;

                if (!firstItem) return stage.clientWidth;

                const secondItem = firstItem.nextElementSibling;

                if (secondItem) {
                    return secondItem.offsetLeft -
                        firstItem.offsetLeft;
                }

                return firstItem.offsetWidth;
            }

            function scrollCarousel(direction) {
                const distance =
                    getItemStep() * itemsPerView * direction;

                stage.scrollBy({
                    left: distance,
                    behavior: 'smooth'
                });
            }

            if (prevButton) {
                prevButton.addEventListener('click', function(event) {
                    event.preventDefault();

                    if (prevButton.classList.contains('disabled')) {
                        return;
                    }

                    scrollCarousel(-1);
                });
            }

            if (nextButton) {
                nextButton.addEventListener('click', function(event) {
                    event.preventDefault();

                    if (nextButton.classList.contains('disabled')) {
                        return;
                    }

                    scrollCarousel(1);
                });
            }

            stage.addEventListener(
                'scroll',
                scheduleArrowUpdate,
                { passive: true }
            );

            carousel.dataset.scrollSnapBound = 'true';

            serverRenderedCarouselInstances.set(stage, {
                scheduleArrowUpdate
            });

            if (serverRenderedCarouselResizeObserver) {
                serverRenderedCarouselResizeObserver.observe(stage);
            }

            /*
            * Run after the browser has completed its initial layout.
            * There were no preceding DOM moves or inline width writes.
            */
            scheduleArrowUpdate();
        });
    }

    function initScrollSnapCarousel(options) {
        options = options || {};
        const breakpoint = options.breakpoint || null; // Optional breakpoint check
        const root = options.root || document;

        // If breakpoint specified, check if we should initialize
        if (breakpoint && window.innerWidth >= breakpoint) {
            return; // Don't initialize above breakpoint
        }

        // The legacy code will continue restructuring carousels on other pages, but it will skip the server-rendered homepage carousels.
        const carousels = root.querySelectorAll('.owl-carousel:not([data-scroll-snap-carousel])' + ':not(.scroll-snap-initialized)');

        carousels.forEach(function(carousel) {
            const wrapper = carousel.closest('.c-Carousel__wrap');
            if (!wrapper) return;

            const prevBtn = wrapper.querySelector('.c-Carousel__toggle-prev');
            const nextBtn = wrapper.querySelector('.c-Carousel__toggle-next');
            const noLoop = carousel.getAttribute('data-no-loop') === 'true';
            const itemsPerView = parseInt(carousel.getAttribute('data-carousel-column')) || 1;
            const autoWidth = carousel.classList.contains('auto-width');

            // Setup carousel structure
            const items = Array.from(carousel.children);
            const stage = document.createElement('div');
            stage.className = 'scroll-snap-stage';

            // Add auto-width class to stage if needed
            if (autoWidth) {
                stage.classList.add('scroll-snap-stage--auto-width');
            }

            // Move all items into stage
            items.forEach(item => stage.appendChild(item));
            carousel.appendChild(stage);

            // Mark as initialized
            carousel.classList.remove('owl-loading');
            carousel.classList.add('scroll-snap-initialized');

            // Calculate scroll amount
            function getScrollAmount() {
                const firstItem = items[0];
                if (!firstItem) return 0;
                const itemWidth = firstItem.offsetWidth;
                const gap = parseInt(getComputedStyle(stage).gap) || 20;

                if (autoWidth) {
                    // For auto-width carousels, scroll by the stage's client width
                    return itemWidth + gap;
                }

                return (itemWidth + gap) * itemsPerView;
            }

            // Update arrow visibility based on scroll position
            function updateArrows() {
                const scrollLeft = stage.scrollLeft;
                const maxScroll = stage.scrollWidth - stage.clientWidth;

                if (!prevBtn || !nextBtn) return;

                // Hide prev arrow at start
                if (scrollLeft <= 1) {
                    prevBtn.classList.add('disabled');
                    prevBtn.style.display = '';
                } else {
                    prevBtn.classList.remove('disabled');
                    prevBtn.style.display = 'flex'; // Revert to CSS (block)
                }

                // Hide next arrow at end
                if (scrollLeft >= maxScroll - 1) {
                    nextBtn.classList.add('disabled');
                    nextBtn.style.display = 'none';
                } else {
                    nextBtn.classList.remove('disabled');
                    nextBtn.style.display = ''; // Revert to CSS (block)
                }
            }

            // Throttle for arrow clicks
            let isThrottled = false;

            if (prevBtn && nextBtn) {
                    // Previous button handler
                prevBtn.addEventListener('click', function(e) {
                        e.preventDefault();
                        if (isThrottled) return;

                        isThrottled = true;
                        setTimeout(() => { isThrottled = false; }, 200);

                    const scrollAmount = getScrollAmount();
                    stage.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
                });

                // Next button handler
                nextBtn.addEventListener('click', function(e) {
                    e.preventDefault();
                    if (isThrottled) return;

                    isThrottled = true;
                    setTimeout(() => { isThrottled = false; }, 200);

                    const scrollAmount = getScrollAmount();
                    stage.scrollBy({ left: scrollAmount, behavior: 'smooth' });
                });
            }

            // Update arrows on scroll
            stage.addEventListener('scroll', () => {
                updateArrows();
            });

            // Update arrows on window resize
            window.addEventListener('resize', updateArrows);

            // Initial arrow state
            updateArrows();
        });

        // Normalize widths after all carousels are initialized
        normalizeCarouselWidths(root);
    }

    // Responsive carousel handler - init/destroy based on breakpoint
    function initResponsiveCarousel(breakpoint) {
        breakpoint = breakpoint || 1260;

        function handleResize() {
            // Only select carousels marked as responsive with data-responsive-carousel="true"
            const carousels = document.querySelectorAll('.owl-carousel[data-responsive-carousel="true"]');

            carousels.forEach(function(carousel) {
                if (window.innerWidth < breakpoint) {
                    // Below breakpoint: initialize carousel if not already
                    if (!carousel.classList.contains('scroll-snap-initialized')) {
                        // Temporarily make it a carousel by wrapping in proper structure
                        const wrapper = carousel.closest('.c-Carousel__wrap');
                        if (wrapper) {
                            initScrollSnapCarousel();
                        }
                    }
                } else {
                    // Above breakpoint: destroy carousel if initialized
                    if (carousel.classList.contains('scroll-snap-initialized')) {
                        destroyScrollSnapCarousel(carousel);
                    }
                }
            });

            // Normalize widths after resize
            normalizeCarouselWidths();
        }

        // Initial check
        handleResize();

        // Debounced resize handler
        const debouncedHandleResize = debounce(handleResize, 250);
        window.addEventListener('resize', debouncedHandleResize);

        return debouncedHandleResize; // Return for potential cleanup
    }

    // Debounced resize handler for width normalization (for non-responsive carousels)
    // Wrapped so the resize Event isn't passed in as the root
    const debouncedNormalizeWidths = debounce(function() { normalizeCarouselWidths(); }, 250);
    window.addEventListener('resize', debouncedNormalizeWidths);

    // Expose Global functions
    window.initScrollSnapCarousel = initScrollSnapCarousel;
    window.destroyScrollSnapCarousel = destroyScrollSnapCarousel;
    window.initResponsiveCarousel = initResponsiveCarousel;

    function initializeCarousels() {
        // New homepage implementation: no DOM movement.
        initServerRenderedCarousels();

        // Temporary compatibility path for unmigrated pages.
        // Only init carousels that don't have responsive behavior
        // Responsive ones should call initResponsiveCarousel() manually
        initScrollSnapCarousel();
    }

    // Initialize when DOM is ready (only for non-responsive carousels)
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initializeCarousels);
    } else {
        initializeCarousels();
    }

})();
